import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { CreatedScimToken } from '@tinct/core-api-models';
import { ApiError, tinctApi } from '../../api';
import { invalidateScimQueries } from './invalidate-scim-queries';

export type CreateScimTokenParams = {
  organizationId: string;
};

export const createScimToken = async (
  params: CreateScimTokenParams,
): Promise<CreatedScimToken> => {
  return await tinctApi.scim.createScimToken(params.organizationId);
};

/**
 * Generates a SCIM token. The answer carries the token's value, once: the caller keeps it in the
 * state of the component that shows it and calls `reset()` when that closes.
 *
 * The hook keeps it out of every cache itself. The tokens list is invalidated, never written from
 * the answer (a refetch brings the new row without its value), and the mutation is garbage-collected
 * as soon as nothing observes it (`gcTime: 0`, which callers cannot override), so the value does
 * not linger in the mutation cache — nor in the query devtools — after the dialog is gone.
 * 409 (5702) when two tokens exist already.
 */
export const useCreateScimToken = (
  options?: Omit<
    UseMutationOptions<CreatedScimToken, ApiError, CreateScimTokenParams>,
    'mutationFn' | 'gcTime'
  >,
) => {
  const { onSuccess, ...restOptions } = options || {};
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createScimToken,
    onSuccess: (...args) => {
      invalidateScimQueries(queryClient, args[1].organizationId, 'tokens');
      onSuccess?.(...args);
    },
    ...restOptions,
    gcTime: 0,
  });
};
